import { t } from '@hefe/i18n';
import {
  AppSlice,
  AuthenticationSlice,
  BasketballCoachesSlice,
  BasketballCoachLoggingSlice,
  BasketballEventsSlice,
  BasketballGamesApi,
  BasketballGamesSlice,
  BasketballLeaguesSlice,
  BasketballMessagesSlice,
  BasketballOperationsCenterApi,
  BasketballPracticesSlice,
  BasketballPracticeTimesApi,
  BasketballPracticeTimesSlice,
  BasketballRefereesSlice,
  BasketballSwitchRequestsSlice,
  BasketballTeamSlice,
  BasketballTeamsSlice,
  BasketballUsersSlice,
  CacheSlice,
  CarsSlice,
  ClubCrudApi,
  ClubSlice,
  DashboardWidgetsSlice,
  ErrorMessages,
  EventHelpoutsApi,
  EventReportsApi,
  EventReportsSlice,
  EventsApi,
  FulfilledMessages,
  GymsSlice,
  InventoryApi,
  InventorySlice,
  IssuesApi,
  KnowledgeBaseApi,
  KnowledgeBaseSlice,
  LocationClosuresApi,
  ModalsSlice,
  PersistSystemSlice,
  ProspectsApi,
  ResetPasswordSlice,
  StatisticsApi,
  StripeApi,
  TablesSlice,
  TasksApi,
  TasksSlice,
  TutorialsApi,
  UserApi,
  userReducer,
  UsersImporterSlice,
  UsersSlice,
} from '@hefe/store';
import {
  combineReducers,
  configureStore,
  Middleware,
  MiddlewareAPI,
} from '@reduxjs/toolkit';
import { setupListeners } from '@reduxjs/toolkit/query';
import { useDispatch } from 'react-redux';
import { persistReducer } from 'redux-persist';
import storage from 'redux-persist/lib/storage';
import { environment } from '../../environments/environment';
import * as CrudApi from '../containers/core/crud/store/crud.api';
import { getMessageApi } from '../containers/core/utils/message/message';

/**
 * Log a warning and show a toast!
 */
export const rtkQueryErrorLogger: Middleware =
  (api: MiddlewareAPI) => (next) => (action: any) => {
    if (action.meta && action.meta.requestStatus === 'fulfilled') {
      const type: string = action.type.substring(
        0,
        action.type.lastIndexOf('/')
      );
      const successMessage: string = FulfilledMessages[type];
      if (successMessage) {
        getMessageApi().success(t('success.' + successMessage), 3);
      }
    } else if (
      action.meta &&
      action.meta.requestStatus === 'rejected' &&
      action?.error?.name !== 'ConditionError'
    ) {
      let type: string = action.type.substring(0, action.type.lastIndexOf('/'));
      const endpointName: string = action?.meta?.arg?.endpointName;

      if (endpointName) {
        type = type + '/' + endpointName;
      }

      let errorMessage: string = ErrorMessages[type];
      if (!errorMessage) {
        errorMessage = 'undefined';
      }

      const errorResponse = action?.error?.message as string;

      if (errorResponse?.startsWith('errors.')) {
        errorMessage = errorResponse.replace('errors.', '');
      } else {
        const payloadResonse = action?.payload?.data?.message as string;
        if (payloadResonse?.startsWith('errors.')) {
          errorMessage = payloadResonse.replace('errors.', '');
        }
      }

      getMessageApi()?.error(t('errors.' + errorMessage), 5);
    }

    return next(action);
  };

const cacheMiddleware: Middleware =
  (api: MiddlewareAPI) => (next) => (action: any) => {
    if (action.meta && action.meta.requestStatus === 'fulfilled') {
      if (action.meta?.arg?.cacheDuration) {
        // Only update cache if data was returned and not undefined
        if (action.payload) {
          const prefixType = action.type.substr(
            0,
            action.type.lastIndexOf('/')
          );
          api.dispatch(
            CacheSlice.cacheActions.setLastModified({
              actionName: prefixType,
              payload: action.meta.arg,
            })
          );
        }
      }
    }

    return next(action);
  };

const reducers = combineReducers({
  // baseReducers
  authentication: AuthenticationSlice.reducer,
  club: ClubSlice.reducer,
  cars: CarsSlice.carsReducer,
  eventReports: EventReportsSlice.reducer,
  dashboardWidgets: DashboardWidgetsSlice.dashboardWidgetsReducer,
  gyms: GymsSlice.gymsReducer,
  inventory: InventorySlice.reducer,
  knowledgeBase: KnowledgeBaseSlice.reducer,
  modals: ModalsSlice.modalsReducer,
  resetPassword: ResetPasswordSlice.resetPasswordReducer,
  tasks: TasksSlice.reducer,
  user: userReducer,
  users: UsersSlice.usersReducer,
  usersImporter: UsersImporterSlice.usersImporterReducer,

  // basketballRedurcers
  basketballCoaches: BasketballCoachesSlice.basketballCoachesReducer,
  basketballCoachLogging:
    BasketballCoachLoggingSlice.basketballCoachLoggingReducer,
  basketballEvents: BasketballEventsSlice.basketballEventsReducer,
  basketballGames: BasketballGamesSlice.basketballGamesReducer,
  basketballLeagues: BasketballLeaguesSlice.basketballLeaguesReducer,
  basketballMessages: BasketballMessagesSlice.basketballMessagesReducer,
  basketballPracticeTimes: BasketballPracticeTimesSlice.reducer,
  basketballPractices: BasketballPracticesSlice.basketballPracticesReducer,
  basketballReferees: BasketballRefereesSlice.basketballRefereesReducer,
  basketballSwitchRequests:
    BasketballSwitchRequestsSlice.basketballSwitchRequestsReducer,
  basketballTeam: BasketballTeamSlice.basketballTeamReducer,
  basketballTeams: BasketballTeamsSlice.basketballTeamsReducer,
  basketballUsers: BasketballUsersSlice.basketballUsersReducer,

  // createApi Reducers
  [EventsApi.reducerPath]: EventsApi.reducer,
  [EventHelpoutsApi.reducerPath]: EventHelpoutsApi.reducer,
  [EventReportsApi.reducerPath]: EventReportsApi.reducer,
  [KnowledgeBaseApi.reducerPath]: KnowledgeBaseApi.reducer,
  [InventoryApi.reducerPath]: InventoryApi.reducer,
  [IssuesApi.reducerPath]: IssuesApi.reducer,
  [LocationClosuresApi.reducerPath]: LocationClosuresApi.reducer,
  [ProspectsApi.reducerPath]: ProspectsApi.reducer,
  [TasksApi.reducerPath]: TasksApi.reducer,
  [TutorialsApi.reducerPath]: TutorialsApi.reducer,
  [StatisticsApi.reducerPath]: StatisticsApi.reducer,
  [StripeApi.reducerPath]: StripeApi.reducer,
  [UserApi.reducerPath]: UserApi.reducer,

  // createApi Basketball Reducers
  [BasketballGamesApi.reducerPath]: BasketballGamesApi.reducer,
  [BasketballOperationsCenterApi.reducerPath]:
    BasketballOperationsCenterApi.reducer,
  [BasketballPracticeTimesApi.reducerPath]: BasketballPracticeTimesApi.reducer,

  // Generic CRUD API
  [ClubCrudApi.reducerPath]: ClubCrudApi.reducer,
  [CrudApi.reducerPath]: CrudApi.reducer,

  // componentReducers
  tables: TablesSlice.tablesReducer,

  // system Reducers
  app: AppSlice.reducer,
  cache: CacheSlice.cacheReducer,
  persistSystem: PersistSystemSlice.reducer,
});

const persistConfig = {
  key: 'root',
  storage: storage,
  whitelist: [
    'app',
    'authentication',
    'dashboardWidgets',
    InventorySlice.FEATURE_KEY,
    KnowledgeBaseSlice.FEATURE_KEY,
    'tables',
    'persistSystem',
  ],
};

const persistedReducer = persistReducer(persistConfig, reducers);

export const store = configureStore({
  reducer: persistedReducer,
  // Additional middleware can be passed to this array
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: false,
    }).concat(
      rtkQueryErrorLogger,
      cacheMiddleware,
      EventsApi.middleware,
      EventHelpoutsApi.middleware,
      EventReportsApi.middleware,
      InventoryApi.middleware,
      IssuesApi.middleware,
      KnowledgeBaseApi.middleware,
      LocationClosuresApi.middleware,
      ProspectsApi.middleware,
      TasksApi.middleware,
      TutorialsApi.middleware,
      StatisticsApi.middleware,
      StripeApi.middleware,
      UserApi.middleware,
      BasketballGamesApi.middleware,
      BasketballOperationsCenterApi.middleware,
      BasketballPracticeTimesApi.middleware,
      ClubCrudApi.middleware,
      CrudApi.middleware
    ),
  devTools: !environment.production, //process.env['NODE_ENV'] !== 'production',
  // Optional Redux store enhancers
  enhancers: (getDefaultEnhancers) => getDefaultEnhancers(),
});

// Enables refetchOnFocus/refetchOnReconnect RTK Query behavior (e.g. issues overview)
setupListeners(store.dispatch);

export type AppDispatch = typeof store.dispatch;

export const useAppDispatch = () => useDispatch<AppDispatch>();
